// The signed-in user (or null) and account actions.
import { create } from "zustand";
import { api, setCsrf } from "./api";
import { useSettings } from "./ui/settings";

export interface Me {
  id: string;
  email: string;
  nickname: string | null;
  isAdmin: boolean;
  skin: "studio" | "hifi" | "zoo" | "eigenbloom";
  reminders: boolean;
  bio: string;
  link: string;
  createdAt: string;
}

interface SessionState {
  user: Me | null;
  loaded: boolean;
  set: (p: Partial<Omit<SessionState, "set">>) => void;
}

export const useSession = create<SessionState>((set) => ({ user: null, loaded: false, set: (p) => set(p) }));

/** Bumped on every sign-in state change, so a slower, older /api/me answer can't overwrite it. */
let generation = 0;

function adopt(user: Me | null, csrf?: string) {
  generation++;
  if (csrf !== undefined) setCsrf(csrf);
  useSession.getState().set({ user, loaded: true });
  // The account's skin wins once signed in.
  if (user && useSettings.getState().skin !== user.skin) useSettings.getState().setSkin(user.skin);
}

export async function loadMe(): Promise<Me | null> {
  const gen = generation;
  try {
    const r = await api<{ user: Me | null; csrfToken: string | null }>("GET", "/api/me");
    if (gen !== generation) return useSession.getState().user;
    adopt(r.user, r.csrfToken ?? undefined);
    if (!r.user) setCsrf(null);
    return r.user;
  } catch (e) {
    useSession.getState().set({ loaded: true });
    throw e;
  }
}

export async function requestLink(email: string, next = "/"): Promise<void> {
  await api("POST", "/api/auth/request", { email, next });
}

/** Create an account: emails a confirmation link (the account exists once it is opened). */
export async function register(email: string, nickname: string, next = "/"): Promise<void> {
  await api("POST", "/api/auth/register", { email, nickname, acceptTerms: true, next });
}

export async function verifyToken(token: string): Promise<{ user: Me; needsNickname: boolean }> {
  const r = await api<{ user: Me; csrfToken: string; needsNickname: boolean }>("POST", "/api/auth/verify", { token });
  if (r.needsNickname) {
    // A new account inherits this browser's skin instead of overriding it.
    const skin = useSettings.getState().skin;
    setCsrf(r.csrfToken);
    if (r.user.skin !== skin) {
      const u = await api<{ user: Me }>("PUT", "/api/me/settings", { skin }).catch(() => null);
      if (u) r.user = u.user;
    }
  }
  adopt(r.user, r.csrfToken);
  return r;
}

export async function setNickname(nickname: string): Promise<Me> {
  const r = await api<{ user: Me }>("PUT", "/api/me/nickname", { nickname });
  adopt(r.user);
  return r.user;
}

export async function saveAccountSettings(p: Partial<Pick<Me, "skin" | "reminders">>): Promise<void> {
  if (!useSession.getState().user) return;
  const r = await api<{ user: Me }>("PUT", "/api/me/settings", p);
  useSession.getState().set({ user: r.user });
}

export async function signOut(): Promise<void> {
  await api("POST", "/api/auth/logout");
  setCsrf(null);
  useSession.getState().set({ user: null });
}

export async function deleteAccount(confirm: string): Promise<{ keptLoops: number; removedLoops: number }> {
  const r = await api<{ keptLoops: number; removedLoops: number }>("DELETE", "/api/me", { confirm });
  setCsrf(null);
  useSession.getState().set({ user: null });
  return r;
}

/** Save the public profile (bio, link). */
export async function saveProfile(bio: string, link: string): Promise<Me> {
  const r = await api<{ user: Me }>("PUT", "/api/me/profile", { bio, link });
  useSession.getState().set({ user: r.user });
  return r.user;
}
